import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import React, { useContext, useState } from 'react'
import CustomDataGrid from '../../../../../components/DataGrid/CustomDataGrid'
import { OvertimeClaimContext } from './OvertimeClaims'
import moment from 'moment'
import { TenantSettingsContext } from '../../../../../App'
import useIsMobile from '../../../../../helpers/useIsMobile'

const Results = () => {
  const [selectedRows, setSelectedRows] = useState([])
  const isMobile = useIsMobile()
  const { orgSettings } = useContext(TenantSettingsContext)?.orgConfig
  const {
    counts,
    results,
    filter,
    setFilter,
    loading,
    setSelectedShift,
    loadMoreClaims,
    loadingMore,
  } = useContext(OvertimeClaimContext)

  const Tab = ({ title }) => (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        flexShrink: 0,
        whiteSpace: 'nowrap',
        cursor: counts[title] ? 'pointer' : 'not-allowed',
        borderBottom: filter.tab === title && 'solid 2px',
        borderColor: 'contrast.main',
      }}
      onClick={() => {
        if (!counts[title]) {
          return null
        }
        setFilter((prev) => ({
          ...prev,
          tab: title,
        }))
        setSelectedRows([])
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: filter.tab === title && 'contrast.main',
          fontWeight: filter.tab === title && 800,
        }}
      >
        {title} ({counts[title]})
      </Typography>
    </Box>
  )

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 4,
        bgcolor: 'background.paper',
        boxShadow: '0 10px 28px rgba(15,23,42,0.06)',
      }}
    >
      <Stack gap={2}>
        <Stack
          direction="row"
          borderBottom="solid 1px rgba(15,23,42,0.16)"
          // Phones: the four tabs don't fit - scroll them rather than wrap
          sx={{
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Tab title="Pending" />
          <Tab title="Approved" />
          <Tab title="Rejected" />
          <Tab title="All Claims" />
        </Stack>
        {loading && <LinearProgress color="contrast" />}
        <CustomDataGrid
          rows={results}
          columns={[
            // {
            //   field: 'checked',
            //   renderHeader: () => (
            //     <Checkbox
            //       checked={selectedRows.length === dislayedResults.length}
            //       onClick={}
            //     />
            //   ),
            // },
            {
              field: 'assignedTo',
              headerName: 'Employee',
              renderCell: ({ row }) => (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Avatar
                    sx={{ width: 24, height: 24 }}
                    src={row.assignedTo.userAccount?.photograph}
                  >
                    {row.assignedTo.userAccount.firstName[0]}
                    {row.assignedTo.userAccount.lastName[0]}
                  </Avatar>
                  <Stack>
                    <Typography variant="body2">
                      {row.assignedTo.userAccount.firstName}{' '}
                      {row.assignedTo.userAccount.lastName}
                    </Typography>
                    <Typography variant="caption">
                      {row.assignedTo.clinicalRole}
                    </Typography>
                  </Stack>
                </Stack>
              ),
              flex: 1,
              minWidth: isMobile ? 140 : 200,
            },
            {
              field: 'shift',
              headerName: 'Shift',
              valueGetter: (value, row) =>
                row.event
                  ? 'Event'
                  : row.ptsDuty
                    ? 'PTS'
                    : row.frontlineDuty
                      ? 'Frontline'
                      : 'Admin',
              renderCell: ({ row }) => (
                <Stack>
                  <Typography variant="body2">
                    {row?.event
                      ? 'Event'
                      : row?.ptsDuty
                        ? 'PTS'
                        : row?.frontlineDuty
                          ? 'Front Line'
                          : 'Admin'}
                  </Typography>
                  <Typography variant="caption">
                    {row?.event?.title ?? ''}
                  </Typography>
                </Stack>
              ),
              minWidth: 60,
            },
            {
              field: 'plannedEnd',
              headerName: 'Planned End',
              valueGetter: (value, row) => moment(row.end).toDate(),
              renderCell: ({ row }) => (
                <Stack>
                  <Typography variant="body2">
                    {moment(row.end).format('HH:mm').toString()}
                  </Typography>
                  <Typography variant="caption">
                    {moment(row.end).format('DD MMM YYYY').toString()}
                  </Typography>
                </Stack>
              ),
              minWidth: isMobile ? 90 : 150,
            },
            {
              field: 'actualEnd',
              headerName: 'Actual End',
              valueGetter: (value, row) =>
                moment(row.overTime.actualFinishTime).toDate(),
              renderCell: ({ row }) => (
                <Stack>
                  <Typography variant="body2">
                    {moment(row.overTime.actualFinishTime)
                      .format('HH:mm')
                      .toString()}
                  </Typography>
                  <Typography variant="caption">
                    {moment(row.overTime.actualFinishTime)
                      .format('DD MMM YYYY')
                      .toString()}
                  </Typography>
                </Stack>
              ),
              minWidth: 150,
            },
            {
              field: 'roundedOvertime',
              headerName: 'Overtime',
              valueGetter: (value, row) =>
                row?.overTime?.decidedMinutes ?? row.overTime.roundedMinutes,
              renderCell: ({ value }) => (
                <Stack>
                  <Typography variant="body2">{value}</Typography>
                  <Typography variant="caption">minutes</Typography>
                </Stack>
              ),
              width: isMobile ? 90 : undefined,
            },
            {
              field: 'rate',
              headerName: `Rate (${orgSettings?.currency ?? '£'})`,
              valueGetter: (value, row) => {
                const minutes =
                  row.overTime?.decidedMinutes ??
                  row.overTime?.roundedMinutes ??
                  0
                if (row?.overTime?.rate) {
                  return ((row.overTime.rate / 60) * minutes).toFixed(2)
                } else if (row.hourlyRate && row.payRate) {
                  return ((row.payRate / 60) * minutes).toFixed(2)
                } else {
                  return 0
                }
              },
              renderCell: ({ value, row }) => {
                const hourlyRateFn = () => {
                  if (row?.overTime?.rate) {
                    return row.overTime.rate
                  } else if (row.hourlyRate && row.payRate) {
                    return row.payRate
                  } else {
                    return 0
                  }
                }
                const hourlyRate = hourlyRateFn()
                return (
                  <Stack>
                    <Typography variant="body2">
                      {orgSettings?.currency ?? '£'}
                      {value}
                    </Typography>
                    {hourlyRate && (
                      <Typography variant="caption">
                        {orgSettings?.currency ?? '£'}
                        {hourlyRate}/hr
                      </Typography>
                    )}
                  </Stack>
                )
              },
            },
            {
              field: 'reason',
              headerName: 'Reason',
              valueGetter: (value, row) =>
                row?.clock?.otherReason ??
                row?.clock?.correctedReason ??
                row?.clock?.lateEndReason ??
                '',
              renderCell: ({ value }) => (
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    lineHeight: 1.4,
                  }}
                >
                  {value}
                </Typography>
              ),
              minWidth: 160,
              flex: 1,
            },
          ]}
          slots={{
            toolbar: () => null,
            footer: () => null,
            noRowsOverlay: () => (
              <Box
                alignItems="center"
                justifyContent="center"
                width="100%"
                mt={2}
              >
                <Alert severity="info">
                  No {filter.tab !== 'All Claims' ? filter.tab : ''} claims
                  found. Staff submit overtime claims from their completed
                  shifts — claims then appear here for review. If you were
                  expecting results, try adjusting the filters above.
                </Alert>
              </Box>
            ),
          }}
          disableColumnMenu
          disableColumnSorting
          // Phones: employee, planned end and overtime identify the claim;
          // the rest is in the claim drawer (no toolbar here to re-show them)
          columnVisibilityModel={
            isMobile
              ? { shift: false, actualEnd: false, rate: false, reason: false }
              : {}
          }
          getRowHeight={() => 'auto'}
          disableRowSelectionOnClick
          onRowClick={({ row }) => setSelectedShift(row)}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#fff',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            },
            '& .MuiDataGrid-main': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              alignItems: 'flex-start',
              py: 1,
            },
          }}
        />
        {!loading && results.length < counts[filter.tab] && (
          <Stack alignItems="center">
            <Button
              variant="outlined"
              onClick={loadMoreClaims}
              disabled={loadingMore}
              sx={{ borderRadius: 2, px: 2 }}
            >
              {loadingMore
                ? 'Loading...'
                : `Load More (${results.length} of ${counts[filter.tab]})`}
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

export default Results
